// Fetch wrappers for all /v1/cms/usage-metrics/* and /v1/cms/usage-logs/* endpoints.
// All functions use hubCall<T> — same pattern as llm-cost.service.ts.
import { hubCall } from "@/lib/api/hub";
import type { ApiResponse } from "@/types/api.types";
import type {
  UsageMetricsFilter,
  UsageSummary,
  UsageTrendPoint,
  UsageByUseCaseRow,
  UsageTopModelRow,
  LlmCallLogRow,
  UsageLogListResp,
  ResourceUsageLogListResp,
} from "../types";

// Converts filter object to a query string, omitting empty/null values.
function toQueryString(params: Record<string, string | number | null | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&");
}

function filtersToParams(f: UsageMetricsFilter): Record<string, string | undefined> {
  return {
    from: f.from ?? undefined,
    to: f.to ?? undefined,
    provider: f.provider || undefined,
    model: f.model || undefined,
    use_case: f.use_case || undefined,
    status: f.status || undefined,
    sourceEntityType: f.sourceEntityType || undefined,
    sourceEntityId: f.sourceEntityId || undefined,
  };
}

export const usageMetricsService = {
  /** GET /v1/cms/usage-metrics/summary */
  async getSummary(f: UsageMetricsFilter): Promise<UsageSummary> {
    const qs = toQueryString(filtersToParams(f));
    const res = await hubCall<ApiResponse<UsageSummary>>(
      `/usage-metrics/summary${qs}`,
      {},
      "Failed to load usage summary",
    );
    return res.data;
  },

  /** GET /v1/cms/usage-metrics/trend?group_by=day|hour */
  async getTrend(f: UsageMetricsFilter, groupBy: "day" | "hour" = "day"): Promise<UsageTrendPoint[]> {
    const qs = toQueryString({ ...filtersToParams(f), group_by: groupBy });
    const res = await hubCall<ApiResponse<UsageTrendPoint[]>>(
      `/usage-metrics/trend${qs}`,
      {},
      "Failed to load usage trend",
    );
    return res.data;
  },

  /** GET /v1/cms/usage-metrics/by-use-case */
  async getByUseCase(f: UsageMetricsFilter): Promise<UsageByUseCaseRow[]> {
    const qs = toQueryString(filtersToParams(f));
    const res = await hubCall<ApiResponse<UsageByUseCaseRow[]>>(
      `/usage-metrics/by-use-case${qs}`,
      {},
      "Failed to load use-case breakdown",
    );
    return res.data;
  },

  /** GET /v1/cms/usage-metrics/top-models?limit=10 */
  async getTopModels(f: UsageMetricsFilter, limit = 10): Promise<UsageTopModelRow[]> {
    const qs = toQueryString({ ...filtersToParams(f), limit });
    const res = await hubCall<ApiResponse<UsageTopModelRow[]>>(
      `/usage-metrics/top-models${qs}`,
      {},
      "Failed to load top models",
    );
    return res.data;
  },

  /** GET /v1/cms/usage-logs — cursor-based list */
  async getLogs(
    f: UsageMetricsFilter,
    cursor?: string | null,
    limit = 50,
  ): Promise<UsageLogListResp> {
    const qs = toQueryString({ ...filtersToParams(f), cursor: cursor ?? undefined, limit });
    const res = await hubCall<ApiResponse<UsageLogListResp>>(
      `/usage-logs${qs}`,
      {},
      "Failed to load usage logs",
    );
    return res.data;
  },

  /** GET /v1/cms/resource-activity/usage-logs — resource-scoped list */
  async getResourceLogs(
    f: UsageMetricsFilter & { sourceEntityType: string; sourceEntityId: string },
    cursor?: string | null,
    limit = 50,
  ): Promise<ResourceUsageLogListResp> {
    const qs = toQueryString({ ...filtersToParams(f), cursor: cursor ?? undefined, limit });
    const res = await hubCall<ApiResponse<ResourceUsageLogListResp>>(
      `/resource-activity/usage-logs${qs}`,
      {},
      "Failed to load resource usage logs",
    );
    return res.data;
  },

  /** GET /v1/cms/usage-logs/:id */
  async getLog(id: string): Promise<LlmCallLogRow> {
    const res = await hubCall<ApiResponse<LlmCallLogRow>>(
      `/usage-logs/${encodeURIComponent(id)}`,
      {},
      "Failed to load log entry",
    );
    return res.data;
  },
};
